/* eslint-disable react/jsx-props-no-spreading */
// Disabling jsx-props-no-spreading: MUI Autocomplete renderInput requires spreading params

import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoIcon from '@mui/icons-material/Info';
import SearchIcon from '@mui/icons-material/Search';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useSnackBar } from '../../hooks/useSnackBar';
import {
  DeviceOS,
  DeviceType,
  LivePreviewEligibility,
  PreviewContext,
  PreviewMode,
  SourceMode,
  STANDARD_CREATIVE_SIZES,
} from '../../types/previewStudio';
import { addMacrosToTag,detectTagType } from '../Creatives/forms/fields/AutoTag/MacroHandler/macroUtils';
import { getVendorName } from '../Creatives/forms/fields/AutoTag/MacroHandler/tagTypes';
import S3Upload from '../forms/S3Upload';

interface ControlsPanelProps {
  context: PreviewContext | null;
  sourceMode: SourceMode;
  onSourceModeChange: (mode: SourceMode) => void;
  selectedCampaignId: number | null;
  onCampaignChange: (id: number | null) => void;
  selectedCreativeId: number | null;
  onCreativeChange: (id: number | null) => void;
  locationType: 'default' | 'custom';
  onLocationTypeChange: (type: 'default' | 'custom') => void;
  selectedLocationId: number | null;
  onLocationChange: (id: number | null) => void;
  customLat: string;
  onCustomLatChange: (lat: string) => void;
  customLon: string;
  onCustomLonChange: (lon: string) => void;
  deviceType: DeviceType;
  onDeviceTypeChange: (type: DeviceType) => void;
  deviceOS: DeviceOS;
  onDeviceOSChange: (os: DeviceOS) => void;
  selectedSize: string;
  onSizeChange: (size: string) => void;
  expandableMode: boolean;
  onExpandableModeChange: (enabled: boolean) => void;
  rawTag: string;
  onRawTagChange: (tag: string) => void;
  campaignUrls: string[];
  onCampaignUrlsChange: (urls: string[]) => void;
  campaignUrlInput: string;
  onCampaignUrlInputChange: (input: string) => void;
  pixelUrls: string[];
  onPixelUrlsChange: (urls: string[]) => void;
  pixelUrlInput: string;
  onPixelUrlInputChange: (input: string) => void;
  onLoadContext?: (campaignId: number | null, creativeId?: number) => Promise<void>;
  isCreativeOnlyMode?: boolean;
  previewMode?: PreviewMode;
  liveEligibility?: LivePreviewEligibility | null;
  fireTracking?: boolean;
  onFireTrackingChange?: (checked: boolean) => void;
  uploadedImageUrl?: string | null;
  onUploadedImageChange?: (url: string | null) => void;
  generateClickTag?: boolean;
  onGenerateClickTagChange?: (enabled: boolean) => void;
}

export default function ControlsPanel({
  context,
  sourceMode,
  onSourceModeChange,
  selectedCampaignId,
  onCampaignChange,
  selectedCreativeId,
  onCreativeChange,
  locationType,
  onLocationTypeChange,
  selectedLocationId,
  onLocationChange,
  customLat,
  onCustomLatChange,
  customLon,
  onCustomLonChange,
  deviceType,
  onDeviceTypeChange,
  deviceOS,
  onDeviceOSChange,
  selectedSize,
  onSizeChange,
  expandableMode,
  onExpandableModeChange,
  rawTag,
  onRawTagChange,
  campaignUrls,
  onCampaignUrlsChange,
  campaignUrlInput,
  onCampaignUrlInputChange,
  pixelUrls,
  onPixelUrlsChange,
  pixelUrlInput,
  onPixelUrlInputChange,
  onLoadContext,
  isCreativeOnlyMode = false,
  previewMode = 'simulated',
  fireTracking = false,
  onFireTrackingChange,
  uploadedImageUrl,
  onUploadedImageChange,
  onGenerateClickTagChange,
}: ControlsPanelProps) {
  const { showSnackBar } = useSnackBar();
  
  // Create a minimal form for S3Upload component (requires FormProvider context)
  const uploadForm = useForm({
    defaultValues: {
      'preview-studio-image': '',
    },
  });
  const [manualCampaignId, setManualCampaignId] = useState<string>(
    selectedCampaignId?.toString() || ''
  );
  const [manualCreativeId, setManualCreativeId] = useState<string>(
    selectedCreativeId?.toString() || ''
  );
  const [isLoadingContext, setIsLoadingContext] = useState(false);
  const [contextError, setContextError] = useState<string | null>(null);
  const [trackingDocsOpen, setTrackingDocsOpen] = useState(false);

  // Generate Macros toggle state
  const [generateMacros, setGenerateMacros] = useState(false);
  const [originalTag, setOriginalTag] = useState('');

  // Determine if we're in creative-only mode based on input fields
  const isActuallyCreativeOnlyMode = isCreativeOnlyMode || (!manualCampaignId.trim() && !!manualCreativeId.trim());

  // Sync manual IDs when selected IDs change externally (but only if they're different)
  // Prevents overwriting user input while they're typing
  React.useEffect(() => {
    if (selectedCampaignId && selectedCampaignId.toString() !== manualCampaignId) {
      setManualCampaignId(selectedCampaignId.toString());
    }
  }, [selectedCampaignId]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (selectedCreativeId && selectedCreativeId.toString() !== manualCreativeId) {
      setManualCreativeId(selectedCreativeId.toString());
    }
  }, [selectedCreativeId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle Generate Macros toggle
  const handleToggleMacros = (checked: boolean) => {
    if (checked && rawTag) {
      setOriginalTag(rawTag);
      const tagType = detectTagType(rawTag);
      if (tagType) {
        try {
          const processedTag = addMacrosToTag(rawTag, tagType);
          onRawTagChange(processedTag);
          setGenerateMacros(true);
          showSnackBar(`Applied macros for ${getVendorName(tagType)} tag`, 'success', { event: 'preview_macros_applied', tag_type: tagType });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
          setGenerateMacros(false);
          showSnackBar(`Error processing tag: ${errorMessage}`, 'warning', { event: 'preview_macros_error', tag_type: tagType });
        }
      } else {
        setGenerateMacros(false);
        showSnackBar('Could not detect tag type. Please ensure the tag is a supported vendor tag.', 'warning', { event: 'preview_macros_error' });
      }
    } else if (!checked && originalTag) {
      onRawTagChange(originalTag);
      setOriginalTag('');
      setGenerateMacros(false);
    }
  };

  const campaign = context?.context.campaign;
  const locations = context?.context.locations || [];
  const targetedCreatives = context?.context.targeted_creatives || [];
  const conquestDestination = context?.context.conquest_destination;

  const handleLoadContext = async () => {
    const campaignId = manualCampaignId.trim() ? parseInt(manualCampaignId.trim(), 10) : null;
    const creativeId = manualCreativeId.trim() ? parseInt(manualCreativeId.trim(), 10) : null;

    // Creative-only mode: load context with just creative ID
    if (isActuallyCreativeOnlyMode) {
      if (!creativeId || Number.isNaN(creativeId)) {
        setContextError('Please enter a valid Creative ID');
        return;
      }
      
      if (!onLoadContext) {
        // Fallback: just set creative ID locally
        onCampaignChange(null);
        onCreativeChange(creativeId);
        return;
      }
      
      try {
        setIsLoadingContext(true);
        setContextError(null);
        // Load context with just creative ID (no campaign ID)
        await onLoadContext(null, creativeId);
        // Update the selected creative ID after context loads successfully
        onCampaignChange(null);
        onCreativeChange(creativeId);
      } catch (err) {
        setContextError(err instanceof Error ? err.message : 'Failed to load context');
      } finally {
        setIsLoadingContext(false);
      }
      return;
    }

    // Campaign mode: campaign ID is required
    if (!campaignId || Number.isNaN(campaignId)) {
      setContextError('Please enter a valid Campaign ID');
      return;
    }

    // Creative ID is optional in campaign mode - user can select from dropdown after context loads
    if (!onLoadContext) {
      // Fallback: just update the campaign ID
      onCampaignChange(campaignId);
      if (creativeId && !Number.isNaN(creativeId)) {
        onCreativeChange(creativeId);
      }
      return;
    }

    try {
      setIsLoadingContext(true);
      setContextError(null);
      // Clear previous context while loading
      // Pass creativeId only if provided (optional)
      await onLoadContext(campaignId, creativeId && !Number.isNaN(creativeId) ? creativeId : undefined);
      // Update the selected IDs after context loads successfully
      onCampaignChange(campaignId);
      if (creativeId && !Number.isNaN(creativeId)) {
        onCreativeChange(creativeId);
      }
    } catch (err) {
      setContextError(err instanceof Error ? err.message : 'Failed to load context');
    } finally {
      setIsLoadingContext(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
          Preview Controls
        </Typography>
        {previewMode === 'live' && (
          <Tooltip title="Live Preview Mode: Generates a real bid response from Venice">
            <InfoIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          </Tooltip>
        )}
      </Box>

      {/* Fire Tracking Checkbox - Only show in live mode */}
      {previewMode === 'live' && onFireTrackingChange && (
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={fireTracking}
                onChange={(e) => onFireTrackingChange(e.target.checked)}
                size="small"
              />
            }
            label="Fire tracking URLs"
          />
          {fireTracking && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Typography variant="caption" sx={{ flex: 1 }}>
                  <strong>Warning:</strong> Verification pixels will fire.
                </Typography>
                <Tooltip title="Learn more about preview tracking behavior">
                  <IconButton
                    size="small"
                    onClick={() => setTrackingDocsOpen(true)}
                    sx={{
                      color: 'warning.main',
                      p: 0.5,
                      '&:hover': {
                        bgcolor: 'warning.light',
                      },
                    }}
                  >
                    <HelpOutlineIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Alert>
          )}
        </Box>
      )}

      {/* Section 1: Source */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>Source</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              value={sourceMode}
              onChange={(e) => onSourceModeChange(e.target.value as SourceMode)}
            >
              <FormControlLabel
                value="campaign"
                control={<Radio size="small" />}
                label={<Typography variant="body2">Campaign / Creative</Typography>}
              />
              <FormControlLabel
                value="raw_tag"
                control={<Radio size="small" />}
                label={<Typography variant="body2">Creative Sandbox</Typography>}
              />
            </RadioGroup>
          </FormControl>

          {sourceMode === 'campaign' && (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Manual ID Entry */}
              <Box>
                <Stack direction="column" spacing={1.5} sx={{ mb: 1 }}>
                  <TextField
                    size="small"
                    label="Campaign ID"
                    type="number"
                    value={manualCampaignId}
                    onChange={(e) => {
                      setManualCampaignId(e.target.value);
                      setContextError(null);
                    }}
                    placeholder="e.g., 263866"
                    fullWidth
                    inputProps={{ min: 1 }}
                    disabled={false}
                  />
                  <TextField
                    size="small"
                    label="Creative ID"
                    type="number"
                    value={manualCreativeId}
                    onChange={(e) => {
                      setManualCreativeId(e.target.value);
                      setContextError(null);
                    }}
                    placeholder="e.g., 254561"
                    fullWidth
                    inputProps={{ min: 1 }}
                    required
                  />
                  {!isActuallyCreativeOnlyMode && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleLoadContext}
                      disabled={isLoadingContext || !manualCampaignId}
                      startIcon={<SearchIcon />}
                      fullWidth
                    >
                      {isLoadingContext ? 'Loading...' : 'Load'}
                    </Button>
                  )}
                  {isActuallyCreativeOnlyMode && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleLoadContext}
                      disabled={isLoadingContext || !manualCreativeId.trim() || Number.isNaN(parseInt(manualCreativeId.trim(), 10)) || parseInt(manualCreativeId.trim(), 10) <= 0}
                      startIcon={<SearchIcon />}
                      fullWidth
                    >
                      {isLoadingContext ? 'Loading...' : 'Load'}
                    </Button>
                  )}
                </Stack>
                {contextError && (
                  <Alert severity="error" sx={{ mt: 1, fontSize: '0.75rem' }}>
                    {contextError}
                  </Alert>
                )}
              </Box>

              {/* Context Display (if loaded) */}
              {context && !isCreativeOnlyMode && (
                <>
                  {/* Campaign Info */}
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
                      Campaign
                    </Typography>
                    <Box
                      sx={{
                        p: 1.5,
                        bgcolor: 'grey.50',
                        borderRadius: 1,
                        border: 1,
                        borderColor: 'grey.300',
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {campaign?.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        ID: {campaign?.id} • Status: {campaign?.status}
                      </Typography>
                      {campaign?.insertion_order_id && (
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                          Insertion Order ID: {campaign.insertion_order_id}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* Creative Selector - Only show when we have a campaign context */}
                  {!isCreativeOnlyMode && targetedCreatives.length > 0 && (
                    <FormControl fullWidth size="small">
                      <InputLabel>Creative</InputLabel>
                      <Select
                        value={selectedCreativeId || ''}
                        onChange={(e) => onCreativeChange(e.target.value as number)}
                        label="Creative"
                      >
                        {targetedCreatives.map((tc) => (
                          <MenuItem key={tc.creative_id} value={tc.creative_id}>
                            <Box>
                              <Typography variant="body2">{tc.creative.name}</Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {tc.creative.size} • {tc.creative.ad_type}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {/* Context Breadcrumb */}
                  <Box
                    sx={{
                      p: 1,
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                      fontSize: '0.7rem',
                      color: 'text.secondary',
                    }}
                  >
                    {context.context.client?.name} → {context.context.advertiser?.name} →{' '}
                    {context.context.contract?.name} → {context.context.insertion_order?.name}
                  </Box>
                </>
              )}

              {/* Creative Only Mode - Show available campaigns from context */}
              {context?.mode === 'creative_only' && context?.context.available_campaigns && context.context.available_campaigns.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Select Campaign (Optional)</InputLabel>
                    <Select
                      value={selectedCampaignId || ''}
                      onChange={async (e) => {
                        const campaignId = e.target.value ? parseInt(e.target.value as string, 10) : null;
                        if (campaignId && onLoadContext && selectedCreativeId) {
                          try {
                            setIsLoadingContext(true);
                            setContextError(null);
                            await onLoadContext(campaignId, selectedCreativeId);
                            onCampaignChange(campaignId);
                            setManualCampaignId(campaignId.toString());
                          } catch (err) {
                            setContextError(err instanceof Error ? err.message : 'Failed to load campaign context');
                          } finally {
                            setIsLoadingContext(false);
                          }
                        } else {
                          onCampaignChange(null);
                          setManualCampaignId('');
                        }
                      }}
                      label="Select Campaign (Optional)"
                      disabled={isLoadingContext}
                    >
                      <MenuItem value="">
                        <em>None (Use QA Mock Data)</em>
                      </MenuItem>
                      {context.context.available_campaigns.map((campaignOption) => (
                        <MenuItem key={campaignOption.id} value={campaignOption.id}>
                          {campaignOption.name} ({campaignOption.status})
                        </MenuItem>
                      ))}
                    </Select>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Select a campaign to load full context. You can also manually enter a Campaign ID above.
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => {
                          // Clear context to allow switching back to campaign mode
                          onCampaignChange(null);
                          setManualCampaignId('');
                          setManualCreativeId('');
                        }}
                        sx={{ minWidth: 'auto', fontSize: '0.7rem', textTransform: 'none' }}
                      >
                        Clear
                      </Button>
                    </Box>
                  </FormControl>
                </Box>
              )}

            </Box>
          )}

          {sourceMode === 'raw_tag' && (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Image Upload for Click Tag */}
              {!uploadedImageUrl ? (
                // eslint-disable-next-line react/jsx-props-no-spreading
                <FormProvider {...uploadForm}>
                  <S3Upload
                    name="preview-studio-image"
                    s3InfoUrl="/admin/helpers/s3_upload_info/"
                    generateKey={() => `preview_studio/${new Date().getTime()}`}
                    accept=".png,.jpg,.jpeg,.gif"
                    onSuccess={(url: string) => {
                      onUploadedImageChange?.(url);
                      onGenerateClickTagChange?.(true); // Automatically enable Generate A Tag
                      showSnackBar('Image uploaded successfully', 'success', { event: 'preview_image_uploaded' });
                    }}
                    onFailure={(error: any) => {
                      showSnackBar(`Upload failed: ${error?.message || 'Unknown error'}`, 'error', { event: 'preview_image_upload_error' });
                    }}
                  />
                </FormProvider>
              ) : (
                <Stack spacing={1}>
                  <Box
                    component="img"
                    src={uploadedImageUrl}
                    alt="Uploaded preview"
                    sx={{
                      maxWidth: '100%',
                      maxHeight: 200,
                      objectFit: 'contain',
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      onUploadedImageChange?.(null);
                      onGenerateClickTagChange?.(false); // Automatically disable Generate A Tag
                      showSnackBar('Image removed', 'info', { event: 'preview_image_removed' });
                    }}
                  >
                    Remove Image
                  </Button>
                </Stack>
              )}
              
              <TextField
                multiline
                rows={8}
                fullWidth
                placeholder="Paste tag HTML / JavaScript here..."
                value={rawTag}
                onChange={(e) => {
                  onRawTagChange(e.target.value);
                  // Reset generate macros toggle if user manually edits the tag
                  if (generateMacros && e.target.value !== rawTag) {
                    setGenerateMacros(false);
                    setOriginalTag('');
                  }
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                  },
                }}
              />
              
              {/* Generate Macros Toggle */}
              <FormControlLabel
                control={
                  <Checkbox 
                    size="small"
                    checked={generateMacros}
                    onChange={(e) => handleToggleMacros(e.target.checked)}
                  />
                }
                label={<Typography variant="body2">Generate Macros</Typography>}
              />
              
              {/* Manual URL Overrides */}
              <Box 
                sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  // Prevent any key events from bubbling up
                  if (e.key === 'Enter') {
                    e.stopPropagation();
                  }
                }}
              >
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={campaignUrls}
                  inputValue={campaignUrlInput}
                  onInputChange={(event, newInputValue, reason) => {
                    onCampaignUrlInputChange(newInputValue || '');
                    // Only prevent default for navigation-related reasons, not for input changes
                    if (event && reason === 'clear') {
                      const syntheticEvent = event as React.SyntheticEvent;
                      syntheticEvent.stopPropagation();
                    }
                  }}
                  selectOnFocus
                  handleHomeEndKeys
                  onChange={(_, newValues) => {
                    // Filter out empty strings and deduplicate
                    const filtered = Array.from(new Set(
                      (newValues as string[]).filter(url => url.trim().length > 0)
                    ));
                    onCampaignUrlsChange(filtered);
                    // Clear input when value is added
                    onCampaignUrlInputChange('');
                  }}
                  onKeyDown={(e) => {
                    // Prevent Enter from causing any navigation
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                  renderInput={(params) => (
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    <TextField
                      {...params}
                      fullWidth
                      placeholder="Add URLs (defaults to https://inmarket.com/)"
                      onKeyDown={(e) => {
                        // Prevent form submission or navigation on Enter
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          e.stopPropagation();
                        }
                      }}
                      inputProps={{
                        ...params.inputProps,
                        style: { fontSize: '0.75rem' },
                        onKeyDown: (e: React.KeyboardEvent) => {
                          // Prevent Enter from causing navigation
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            e.stopPropagation();
                          }
                        },
                      }}
                    />
                  )}
                  renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => (
                      <Chip
                        key={option}
                        label={option}
                        size="small"
                        deleteIcon={<CloseIcon fontSize="small" />}
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  sx={{
                    '& .MuiChip-root': {
                      height: 24,
                      fontSize: '0.75rem',
                      margin: 0.5,
                    },
                  }}
                />
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={pixelUrls}
                  inputValue={pixelUrlInput}
                  onInputChange={(event, newInputValue, reason) => {
                    onPixelUrlInputChange(newInputValue || '');
                    // Only prevent default for navigation-related reasons, not for input changes
                    if (event && reason === 'clear') {
                      const syntheticEvent = event as React.SyntheticEvent;
                      syntheticEvent.stopPropagation();
                    }
                  }}
                  selectOnFocus
                  handleHomeEndKeys
                  onChange={(_, newValues) => {
                    // Filter out empty strings and deduplicate
                    const filtered = Array.from(new Set(
                      (newValues as string[]).filter(url => url.trim().length > 0)
                    ));
                    onPixelUrlsChange(filtered);
                    // Clear input when value is added
                    onPixelUrlInputChange('');
                  }}
                  onKeyDown={(e) => {
                    // Prevent Enter from causing any navigation
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                  renderInput={(params) => (
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    <TextField
                      {...params}
                      fullWidth
                      placeholder="Pixel URLs"
                      onKeyDown={(e) => {
                        // Prevent form submission or navigation on Enter
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          e.stopPropagation();
                        }
                      }}
                      inputProps={{
                        ...params.inputProps,
                        style: { fontSize: '0.75rem' },
                        onKeyDown: (e: React.KeyboardEvent) => {
                          // Prevent Enter from causing navigation
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            e.stopPropagation();
                          }
                        },
                      }}
                    />
                  )}
                  renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => (
                      <Chip
                        key={option}
                        label={option}
                        size="small"
                        deleteIcon={<CloseIcon fontSize="small" />}
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  sx={{
                    '& .MuiChip-root': {
                      height: 24,
                      fontSize: '0.75rem',
                      margin: 0.5,
                    },
                  }}
                />
              </Box>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Section 2: Device & Context */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>Device & Context</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Device Type */}
            <FormControl fullWidth size="small">
              <InputLabel>Device Type</InputLabel>
              <Select
                value={deviceType}
                onChange={(e) => onDeviceTypeChange(e.target.value as DeviceType)}
                label="Device Type"
              >
                <MenuItem value="mobile">📱 Mobile</MenuItem>
                <MenuItem value="tablet">📱 Tablet</MenuItem>
                <MenuItem value="desktop">💻 Desktop</MenuItem>
                <MenuItem value="ctv">📺 CTV</MenuItem>
              </Select>
            </FormControl>

            {/* OS (for mobile/tablet) */}
            {(deviceType === 'mobile' || deviceType === 'tablet') && (
              <FormControl fullWidth size="small">
                <InputLabel>Operating System</InputLabel>
                <Select
                  value={deviceOS}
                  onChange={(e) => onDeviceOSChange(e.target.value as DeviceOS)}
                  label="Operating System"
                >
                  <MenuItem value="ios">iOS</MenuItem>
                  <MenuItem value="android">Android</MenuItem>
                </Select>
              </FormControl>
            )}

            {/* Creative Size */}
            <FormControl fullWidth size="small">
              <InputLabel>Creative Size</InputLabel>
              <Select
                value={selectedSize}
                onChange={(e) => onSizeChange(e.target.value)}
                label="Creative Size"
              >
                {STANDARD_CREATIVE_SIZES.map((size) => (
                  <MenuItem key={size.label} value={`${size.width}x${size.height}`}>
                    {size.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Expandable Mode Toggle */}
            <Box
              sx={{
                bgcolor: '#f9f9f9',
                borderRadius: 1,
                p: 1.5,
                border: '1px solid #e0e0e0',
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={expandableMode}
                    onChange={(e) => onExpandableModeChange(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      MRAID Ad
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Toggle if preview sizing or animations look off
                    </Typography>
                  </Box>
                }
              />
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Section 3: Location */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>Location</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              value={locationType}
              onChange={(e) => onLocationTypeChange(e.target.value as 'default' | 'custom')}
            >
              {!isCreativeOnlyMode && (
                <FormControlLabel
                  value="default"
                  control={<Radio size="small" />}
                  label={<Typography variant="body2">Campaign Default</Typography>}
                />
              )}
              <FormControlLabel
                value="custom"
                control={<Radio size="small" />}
                label={<Typography variant="body2">Custom Coordinates</Typography>}
              />
            </RadioGroup>
          </FormControl>

          {locationType === 'default' && !isCreativeOnlyMode && locations.length > 0 && (
            <Box sx={{ mt: 2 }}>
              {(() => {
                // Filter to only locations with valid coordinates for simulation
                const validLocations = locations.filter(
                  (loc) => typeof loc.latitude === 'number' && typeof loc.longitude === 'number'
                );
                const hasNoValidLocations = validLocations.length === 0;

                return (
                  <>
                    {hasNoValidLocations && (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="caption">
                          No location list detected. Locations without coordinates (e.g., targeting by zipcode or user segments) will use default coordinates for simulation.
                        </Typography>
                      </Alert>
                    )}
                    <FormControl fullWidth size="small">
                      <InputLabel>Location</InputLabel>
                      <Select
                        value={selectedLocationId || ''}
                        onChange={(e) => onLocationChange(e.target.value as number)}
                        label="Location"
                      >
                        {locations.map((loc) => {
                          const hasLat = typeof loc.latitude === 'number';
                          const hasLon = typeof loc.longitude === 'number';

                          let coordLabel = 'Coordinates not set';
                          if (hasLat && hasLon) {
                            coordLabel = `${loc.latitude.toFixed(6)}, ${loc.longitude.toFixed(6)}`;
                          } else if (hasLat) {
                            coordLabel = `${loc.latitude.toFixed(6)}, N/A`;
                          } else if (hasLon) {
                            coordLabel = `N/A, ${loc.longitude.toFixed(6)}`;
                          }

                          return (
                            <MenuItem key={loc.id} value={loc.id}>
                              <Box>
                                <Typography variant="body2">{loc.name}</Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                  {coordLabel}
                                </Typography>
                              </Box>
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </>
                );
              })()}

              {campaign?.is_conquest && conquestDestination && (
                <Box
                  sx={{
                    mt: 1.5,
                    p: 1,
                    bgcolor: 'info.lighter',
                    borderRadius: 1,
                    border: 1,
                    borderColor: 'info.light',
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                    Conquest Destination
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {conquestDestination.name}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                    {typeof conquestDestination.latitude === 'number' && typeof conquestDestination.longitude === 'number'
                      ? `${conquestDestination.latitude.toFixed(6)}, ${conquestDestination.longitude.toFixed(6)}`
                      : 'Coordinates not set'}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {locationType === 'custom' && (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <TextField
                fullWidth
                size="small"
                label="Latitude"
                type="number"
                value={customLat}
                onChange={(e) => onCustomLatChange(e.target.value)}
                placeholder="34.010977"
                inputProps={{ step: 0.000001, min: -90, max: 90 }}
                disabled={previewMode === 'live'}
                helperText={previewMode === 'live' ? 'Custom coordinates disabled in Live mode' : ''}
              />
              <TextField
                fullWidth
                size="small"
                label="Longitude"
                type="number"
                value={customLon}
                onChange={(e) => onCustomLonChange(e.target.value)}
                placeholder="-118.387985"
                inputProps={{ step: 0.000001, min: -180, max: 180 }}
                disabled={previewMode === 'live'}
                helperText={previewMode === 'live' ? 'Custom coordinates disabled in Live mode' : ''}
              />
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Section 4: Privacy */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>Privacy</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <FormControlLabel
              control={<Checkbox size="small" />}
              label={<Typography variant="body2">GDPR Enabled</Typography>}
            />
            <FormControlLabel
              control={<Checkbox size="small" />}
              label={<Typography variant="body2">CCPA / US Privacy</Typography>}
            />
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Preview Tracking Documentation Dialog */}
      <Dialog
        open={trackingDocsOpen}
        onClose={() => setTrackingDocsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Preview Tracking Behavior</Typography>
            <IconButton
              aria-label="close"
              onClick={() => setTrackingDocsOpen(false)}
              sx={{ color: 'text.secondary' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3}>
            {/* Ad Serving Pixels Section */}
            <Box>
              <Typography variant="h6" sx={{ mb: 1, fontSize: '1rem', fontWeight: 600 }}>
                Ad Serving Pixels (Always Disabled)
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                These are set to test mode and won&apos;t record impressions:
              </Typography>
              <Stack component="ul" spacing={0.5} sx={{ pl: 2, m: 0 }}>
                <Typography component="li" variant="body2">
                  DCM/CM360 impression trackers
                </Typography>
                <Typography component="li" variant="body2">
                  Flashtalking
                </Typography>
                <Typography component="li" variant="body2">
                  Sizmek
                </Typography>
              </Stack>
            </Box>

            {/* Verification Pixels Section */}
            <Box>
              <Typography variant="h6" sx={{ mb: 1, fontSize: '1rem', fontWeight: 600 }}>
                Verification Pixels (Stripped by Default)
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                When &quot;Fire tracking URLs&quot; is OFF, these are removed:
              </Typography>
              <Stack component="ul" spacing={0.5} sx={{ pl: 2, m: 0 }}>
                <Typography component="li" variant="body2">
                  IAS (Integral Ad Science)
                </Typography>
                <Typography component="li" variant="body2">
                  DoubleVerify
                </Typography>
                <Typography component="li" variant="body2">
                  Moat
                </Typography>
                <Typography component="li" variant="body2">
                  Nielsen
                </Typography>
                <Typography component="li" variant="body2">
                  comScore
                </Typography>
                <Typography component="li" variant="body2">
                  And other measurement vendors
                </Typography>
              </Stack>
            </Box>

            {/* Best Practice */}
            <Box>
              <Typography variant="h6" sx={{ mb: 1, fontSize: '1rem', fontWeight: 600 }}>
                Best Practice
              </Typography>
              <Typography variant="body2">
                Leave &quot;Fire tracking URLs&quot; unchecked unless specifically testing verification vendor integration.
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTrackingDocsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

